import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Table, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Switch, 
  CircularProgress, 
  TextField, 
  Button, 
  Fab, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';


const AdminAdmins = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    org_name: '',
    first_name: '',
    last_name: '',
    phone_number: ''
  });

  const navigate = useNavigate();
  
  const fetchAdminDashboard = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    setLoading(false);

    try {
      const response = await axios.get(`${API_BASE_URL}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setStats(response.data);
      setLoading(false);

    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      }
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchAdminDashboard();
  }, []);

  const handleToggle = async (email) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/admins/${email}`, {
        is_enabled: !stats.find(admin => admin.email === email).is_enabled,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status !== 200) {
        // pop up error message
        alert('Error updating admin status');
        return;
      }

      window.location.reload();

      const updatedStats = [...stats];
      updatedStats.forEach(admin => {
        if (admin.email === email) {
          admin.is_enabled = !admin.is_enabled;
        }
      });
      setStats(updatedStats);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      }
      console.error('Error updating admin status:', error);
    }
  };

  const handleFieldChange = (email, field, value) => {
    setStats(prevStats => 
      prevStats.map(admin => 
        admin.email === email 
          ? { ...admin, [field]: field === 'is_enabled' ? Number(value) : value } 
          : admin
      )
    );
  };

  const handleSave = async (email) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {

      const admin = stats.find(admin => admin.email === email);

      const payload = {
        email: email,
        max_org: admin.max_org,
        max_job_flow: admin.max_job_flow,
        max_role: admin.max_role,
        max_member: admin.max_member,
        max_product_department: admin.max_product_department,
        max_product_category: admin.max_product_category,
        max_product_item: admin.max_product_item,
        max_customer: admin.max_customer,
        max_order: admin.max_order,
        max_order_jobs: admin.max_order_jobs,
        max_job_note: admin.max_job_note,
        max_form_defination_item: admin.max_form_defination_item,
      };

      const response = await axios.post(`${API_BASE_URL}/admin/admins-params`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      if (response.status !== 200) {
        // pop up error message
        alert('Error updating admin params');
        return;
      }

      window.location.reload();
    } catch (error) {
      console.error('Error updating admin:', error);
      throw error;
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewAdmin({ email: '', password: '', org_name: '' });
  };

  const handleNewAdminChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin(prev => ({ ...prev, [name]: value }));
  };

  const handleAddAdmin = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/new`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 201) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error adding new admin:', error);
    }
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{ maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Orgs</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Job Flows</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Roles</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Members</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product Departments</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product Categories</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customers</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Orders</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Jobs</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Job Notes</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Form Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Enabled</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.map((admin) => (
                <TableRow key={admin.email}>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_org} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_org', e.target.value)}
                      />
                    ) : admin.max_org}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_job_flow} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_job_flow', e.target.value)}
                      />
                    ) : admin.max_job_flow}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_role} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_role', e.target.value)}
                      />
                    ) : admin.max_role}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_member} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_member', e.target.value)}
                      />
                    ) : admin.max_member}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_product_department} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_product_department', e.target.value)}
                      />
                    ) : admin.max_product_department}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_product_category} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_product_category', e.target.value)}
                      />
                    ) : admin.max_product_category}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_product_item} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_product_item', e.target.value)}
                      />
                    ) : admin.max_product_item}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_customer} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_customer', e.target.value)}
                      />
                    ) : admin.max_customer}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_order} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_order', e.target.value)}
                      />
                    ) : admin.max_order}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_order_jobs} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_order_jobs', e.target.value)}
                      />
                    ) : admin.max_order_jobs}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_job_note} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_job_note', e.target.value)}
                      />
                    ) : admin.max_job_note}
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <TextField 
                        size="small"
                        value={admin.max_form_defination_item} 
                        onChange={(e) => handleFieldChange(admin.email, 'max_form_defination_item', e.target.value)}
                      />
                    ) : admin.max_form_defination_item}
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={admin.is_enabled} 
                      onChange={() => handleToggle(admin.email)}
                    />
                  </TableCell>
                  <TableCell>
                    {editing === admin.email ? (
                      <>
                        <Button 
                          variant="contained" 
                          color="success" 
                          size="small"
                          onClick={() => handleSave(admin.email)}
                          sx={{ mr: 1 }}
                        >
                          Save
                        </Button>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          size="small"
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => setEditing(admin.email)}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Fab 
        color="primary" 
        aria-label="add" 
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleOpenDialog}
      >
        <AddIcon />
      </Fab>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{color: 'black'}}>Add New Admin</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="standard"
            value={newAdmin.email}
            onChange={handleNewAdminChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="password"
            label="Password"
            type="password"
            fullWidth
            variant="standard"
            value={newAdmin.password}
            onChange={handleNewAdminChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="org_name"
            label="Organization Name"
            fullWidth
            variant="standard"
            value={newAdmin.org_name}
            onChange={handleNewAdminChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="first_name"
            label="First Name"
            fullWidth
            variant="standard"
            value={newAdmin.first_name}
            onChange={handleNewAdminChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="last_name"
            label="Last Name"
            fullWidth
            variant="standard"
            value={newAdmin.last_name}
            onChange={handleNewAdminChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="phone_number"
            label="Phone Number"
            fullWidth
            variant="standard"
            value={newAdmin.phone_number}
            onChange={handleNewAdminChange}
            multiline
            rows={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleAddAdmin}>Add Admin</Button>
        </DialogActions>
      </Dialog>
          </Box>
        );
      };

export default AdminAdmins;
